/**
 * Phones: swipe the cart sheet down to close it.
 * The sheet follows the finger; released past 80px (or flicked) it closes through
 * Fabric's own closeDialog, otherwise it slides back.
 */
const mobile = window.matchMedia('(max-width: 749px)');
const CLOSE_DISTANCE = 80;
const CLOSE_SPEED = 0.5; // px per ms
const START_SLOP = 10;
const FULL = 'data-ejanse-full';

/** "vis hele kurven" / "vis mindre": the sheet grows to the full screen and back (styles in ejanse.css). */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ejanse-cart-expand]') : null;
  button?.closest('dialog')?.toggleAttribute(FULL);
});

/** The cart always opens as the half-screen sheet. */
document.addEventListener(
  'dialog:close',
  (event) => {
    if (event.target instanceof Element) event.target.querySelector(`dialog[${FULL}]`)?.removeAttribute(FULL);
  },
  { capture: true }
);

/** @type {{ dialog: HTMLDialogElement, startY: number, startTime: number, dy: number, dragging: boolean } | null} */
let drag = null;

/** True if the touch started inside something that is scrolled away from its top. */
function insideScrolled(target, dialog) {
  for (let el = target; el && el !== dialog; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return false;
}

function settle(dialog) {
  dialog.style.transition = '';
  dialog.style.translate = '';
}

function onTouchStart(event) {
  drag = null;
  const dialog = event.currentTarget;
  if (!mobile.matches || event.touches.length !== 1 || !dialog.open) return;
  const target = /** @type {Element} */ (event.target);
  // target === dialog means the transparent area above the sheet; a tap there closes it (Fabric).
  if (target === dialog || dialog.classList.contains('dialog-closing')) return;
  if (insideScrolled(target, dialog)) return;
  // A press on a button, link or field is never a swipe: a thumb that rolls a little must still count as a tap.
  if (target.closest('button, a, input, select, textarea, label, summary')) return;
  drag = { dialog, startY: event.touches[0].clientY, startTime: event.timeStamp, dy: 0, dragging: false };
}

function onTouchMove(event) {
  if (!drag) return;
  const dy = event.touches[0].clientY - drag.startY;
  if (!drag.dragging) {
    if (dy < -START_SLOP) {
      drag = null; // scrolling up through the cart
      return;
    }
    if (dy < START_SLOP) return;
    drag.dragging = true;
    drag.dialog.style.transition = 'none';
    drag.startY += START_SLOP;
    drag.startTime = event.timeStamp;
  }
  if (event.cancelable) event.preventDefault();
  drag.dy = Math.max(0, event.touches[0].clientY - drag.startY);
  drag.dialog.style.translate = `0 ${drag.dy}px`;
}

function release(event) {
  if (!drag) return;
  const { dialog, dy, dragging, startTime } = drag;
  drag = null;
  if (!dragging) {
    settle(dialog);
    return;
  }
  const speed = dy / Math.max(1, event.timeStamp - startTime);
  if ((dy > CLOSE_DISTANCE || speed > CLOSE_SPEED) && dialog.hasAttribute(FULL)) {
    // Full screen: a swipe down goes back to the half-screen sheet first.
    dialog.removeAttribute(FULL);
    settle(dialog);
  } else if (dy > CLOSE_DISTANCE || speed > CLOSE_SPEED) {
    // The closing animation runs on transform, on top of this translate, so the sheet leaves from where the finger let go.
    dialog.style.transition = '';
    const component = /** @type {any} */ (dialog.closest('cart-drawer-component'));
    if (!component) return settle(dialog);
    component.addEventListener('dialog:close', () => settle(dialog), { once: true });
    component.closeDialog();
  } else {
    dialog.style.transition = 'translate 250ms cubic-bezier(0.4, 0, 0.2, 1)';
    dialog.style.translate = '';
    dialog.addEventListener('transitionend', () => settle(dialog), { once: true });
  }
}

/**
 * The touch listeners sit on the cart itself, never on the page. touchmove has to be able to stop
 * the scroll while the sheet is dragged, and a listener like that on the document would make the
 * browser wait for this script before every scroll, on every page.
 */
const bound = new WeakSet();

function bind(dialog) {
  if (!(dialog instanceof HTMLDialogElement) || bound.has(dialog)) return;
  bound.add(dialog);
  dialog.addEventListener('touchstart', onTouchStart, { passive: true });
  dialog.addEventListener('touchmove', onTouchMove, { passive: false });
  dialog.addEventListener('touchend', release, { passive: true });
  dialog.addEventListener('touchcancel', release, { passive: true });
}

document.querySelectorAll('dialog.cart-drawer__dialog').forEach(bind);
// Covers a cart that was rendered again after this script ran.
document.addEventListener(
  'dialog:open',
  (event) => {
    if (event.target instanceof Element) bind(event.target.querySelector('dialog.cart-drawer__dialog'));
  },
  { capture: true }
);

/**
 * On phones Fabric adds a history entry when the cart opens and steps back when it closes.
 * The browser then restores the scroll position it saved while the page was locked (the top),
 * which shows now that the page is visible above the sheet. Turn that off for the page's own
 * entry before Fabric adds the cart's (this capture listener runs first); Fabric restores the
 * scroll position itself.
 */
document.addEventListener(
  'dialog:open',
  (event) => {
    if (!mobile.matches || !(event.target instanceof Element) || event.target.localName !== 'cart-drawer-component') return;
    if (history.state?.cartDrawerOpen || history.scrollRestoration === 'manual') return;
    history.scrollRestoration = 'manual';
    const restore = (event) => {
      window.removeEventListener('popstate', restore);
      window.removeEventListener('pagehide', restore);
      // The browser restores scroll just after popstate, so switch back a moment later.
      if (event.type === 'pagehide') history.scrollRestoration = 'auto';
      else setTimeout(() => (history.scrollRestoration = 'auto'), 500);
    };
    window.addEventListener('popstate', restore);
    window.addEventListener('pagehide', restore);
  },
  { capture: true }
);
